import BidirectionalSort from '@app/components/BidirectionalSort'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { AssociatedProperty } from '@app/models/AssociatedProperty'
import { SuccessStory } from '@app/models/SuccessStory'
import {
  IGlobalConfigurationState,
  IGlobalHbUserState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import {
  formatAddressSingleLine,
  formatDate,
  humanize,
} from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import {
  actionCreators,
  ISuccessStoriesListState,
} from '../modules/successStoriesList'

type ComponentProps = ISuccessStoriesListState &
  IGlobalConfigurationState &
  IGlobalHbUserState &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class SuccessStoriesListPage extends React.PureComponent<ComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)
    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public componentDidUpdate(prevProps: ComponentProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public render() {
    return (
      <DocumentTitle title='Success Stories'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div className='mb-2 d-flex flex-row justify-content-between'>
            <h3>Success Stories</h3>
            <Link to='successStory/add' className='align-self-center'>
              <Button size='sm' color='primary'>
                <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                <span>Add Success Story</span>
              </Button>
            </Link>
          </div>
          <Row>
            <Col>
              <SearchBar
                value={this.props.filterText}
                onChange={this._onChangeFilter}
                className='pr-3'
              />
            </Col>
          </Row>
          <Row className='pt-3'>
            <Col>
              <FormGroup>
                <Label>Sort By</Label>
                <Input
                  type='select'
                  bsSize='sm'
                  value={this.props.sortOrder}
                  onChange={this._onChangeSortOrder}
                >
                  {['Alphabetical', 'DateCreated', 'PropertyName'].map(
                    (p, i) => (
                      <option key={`sort-${i}`} value={p}>
                        {humanize(p)}
                      </option>
                    ),
                  )}
                </Input>
              </FormGroup>
            </Col>
            <Col>
              <BidirectionalSort
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
            </Col>
          </Row>
          <InfiniteScrollTable
            isLoading={this.props.isLoading}
            items={this.props.items}
            loadItems={this.props.loadSuccessStories}
            noRowsRenderer={this._noRowsRenderer}
            onRowClicked={this._onRowClicked}
            totalItems={this.props.totalItems}
            ref={this.tableRef}
            rowHeight={65}
          >
            <Column
              cellRenderer={({ cellData }) => {
                return <div className='medium text-wrap'>{cellData}</div>
              }}
              label='Title'
              dataKey='title'
              flexGrow={1}
              flexShrink={1}
              width={30}
            />
            <Column
              cellRenderer={({ cellData }) => {
                return (
                  <div>
                    <div className='font-weight-bold text-truncate small'>
                      {cellData.name}
                    </div>
                    <div className='small text-truncate'>
                      {formatAddressSingleLine(
                        (cellData as AssociatedProperty).address,
                      )}
                    </div>
                  </div>
                )
              }}
              dataKey='property'
              label='Property'
              flexGrow={1}
              flexShrink={1}
              width={30}
            />
            <Column
              cellRenderer={({ cellData }) => {
                return (
                  <div className='medium'>
                    {formatDate(cellData, 'MM/DD/YYYY')}
                  </div>
                )
              }}
              label='Date Created'
              dataKey='dateCreated'
              flexGrow={0}
              flexShrink={1}
              width={105}
            />
          </InfiniteScrollTable>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No success stories</div>
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }

  private _onRowClicked(item: SuccessStory) {
    window.open(
      `${window.location.pathname.replace(
        'properties/successStories',
        'properties/successStory',
      )}/${item.id}`,
    )
  }
}
